<template>
  <div class="create-chatbot">
    <!-- Utilisation du composant Notification -->
    <Notification 
      v-model:show="showNotification" 
      :message="notificationMessage" 
      :type="notificationType"
    />

    <header class="content-header">
      <div class="header-title">
        <h1>Créer un nouveau chatbot</h1>
        <div class="header-subtitle">CONFIGURATION INITIALE DU CHATBOT</div>
      </div>
      <div class="header-actions">
        <button class="back-btn" @click="$router.back()">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          <span>Retour</span>
        </button>
      </div>
    </header>

    <div v-if="loading" class="loading-state">
      Création du chatbot en cours...
    </div>
    
    <div v-else-if="error" class="error-state">
      {{ error }}
    </div>
    
    <div v-else class="create-content">
      <div class="settings-section">
        <h2>Informations générales</h2>
        <div class="settings-grid">
          <div class="avatar-container">
            <div class="avatar-row">
              <label for="avatar-upload" class="avatar-preview" title="Cliquez pour changer l'avatar">
                <img :src="avatarPreview || 'https://placehold.co/250'" alt="Avatar du chatbot">
                <div class="avatar-overlay">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="17 8 12 3 7 8"></polyline>
                    <line x1="12" y1="3" x2="12" y2="15"></line>
                  </svg>
                </div>
              </label>
              <input 
                id="avatar-upload" 
                type="file" 
                accept="image/jpeg,image/jpg,image/png" 
                @change="handleAvatarUpload" 
                style="display: none;"
              >
            </div>
          </div>
          <div class="form-group">
            <label>Nom du chatbot</label>
            <input type="text" v-model="chatbot.bot_name" placeholder="Nom du chatbot">
          </div>
          <div class="form-group">
            <label>Type</label>
            <select v-model="chatbot.type">
              <option value="whatsapp">WhatsApp</option>
              <option value="telegram">Telegram</option>
              <option value="messenger">Messenger</option>
              <option value="web">Web</option>
            </select>
          </div>
          <div class="form-group">
            <label>Statut</label>
            <select v-model="chatbot.status">
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
              <option value="maintenance">En maintenance</option>
            </select>
          </div>
          <div class="form-group">
            <label>Description</label>
            <textarea v-model="chatbot.description" placeholder="Description du chatbot"></textarea>
          </div>
          <div class="form-group">
            <label>Email</label>
            <input type="email" v-model="chatbot.email" placeholder="Email">
          </div>
        </div>
      </div>
      
      <!-- Configuration WhatsApp -->
      <div v-if="chatbot.type === 'whatsapp'" class="settings-section whatsapp-section">
        <div class="section-header" @click="toggleWhatsAppConfig">
          <h2>Configuration WhatsApp</h2>
          <button class="toggle-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline :points="showWhatsAppConfig ? '18 15 12 9 6 15' : '6 9 12 15 18 9'"></polyline>
            </svg>
          </button>
        </div>
        <div v-if="showWhatsAppConfig" class="settings-grid">
          <div class="form-group">
            <label>Numéro de téléphone affiché</label>
            <PhoneInput
              v-model="chatbot.display_phone_number"
              @country-change="handlePhoneChange"
              placeholder="Numéro de téléphone"
            />
          </div>
          <div class="form-group">
            <label>ID du numéro de téléphone</label>
            <input type="text" v-model="chatbot.phone_number_id" placeholder="ID du numéro de téléphone">
          </div>
          <div class="form-group">
            <label>ID de l'application</label>
            <input type="text" v-model="chatbot.app_id" placeholder="ID de l'application">
          </div>
          <div class="form-group full-width">
            <label>Token WhatsApp</label>
            <div class="copy-input">
              <input :type="showPassword ? 'text' : 'password'" v-model="chatbot.access_token.env.WHATSAPP_TOKEN" placeholder="Token WhatsApp">
              <button class="toggle-password" @click="togglePasswordVisibility" title="Afficher/Masquer">
                <svg v-if="showPassword" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                  <line x1="1" y1="1" x2="23" y2="23"></line>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div class="settings-actions">
        <button class="cancel-btn" @click="$router.back()">Annuler</button>
        <button class="save-btn" @click="createChatbot">Créer</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import PhoneInput from './PhoneInput.vue';
import Notification from './Notification.vue';

import { apiBaseUrl } from '@/config';

// Importation dynamique d'axios
let axios;
import('axios').then(module => {
  axios = module.default;
  
}).catch(err => {
  console.error('Erreur lors de l\'importation d\'axios:', err);
});



// Récupérer les paramètres de l'URL
const route = useRoute();

// État du formulaire
const chatbot = ref({
  bot_name: '',
  type: 'whatsapp',
  status: 'inactive',
  description: '',
  email: '',
  avatar: '',
  display_phone_number: '',
  phone_number_id: '',
  app_id: '',
  access_token: {
    env: {
      WHATSAPP_TOKEN: ''
    }
  }
});

const loading = ref(false);
const error = ref(null);
const showPassword = ref(false);
const showNotification = ref(false);
const notificationMessage = ref('');
const notificationType = ref('success');
const avatarPreview = ref(null);
const phoneCountry = ref(null);
const showWhatsAppConfig = ref(false);

const showNotificationMessage = (message, type = 'success') => {
  notificationMessage.value = message;
  notificationType.value = type;
  showNotification.value = true;
};

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};

const toggleWhatsAppConfig = () => {
  showWhatsAppConfig.value = !showWhatsAppConfig.value;
};

const handleAvatarUpload = async (event) => {
  const file = event.target.files[0];
  
  if (!file) {
    return;
  }
  
  // Vérifier que c'est bien une image et pas un SVG
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
  if (!allowedTypes.includes(file.type)) {
    showNotificationMessage('Format non supporté. Veuillez utiliser JPG, JPEG ou PNG');
    return;
  }
  
  // Créer une URL pour l'aperçu
  avatarPreview.value = URL.createObjectURL(file);
  
  // Stocker le fichier pour l'upload ultérieur
  chatbot.value.avatarFile = file;
};

const handlePhoneChange = (country) => {
  
  phoneCountry.value = country;
};

const createChatbot = async () => {
  try {
    
    
    // Vérifier si les champs obligatoires sont remplis
    if (!chatbot.value.bot_name) {
      
      showNotificationMessage('Le nom du chatbot est obligatoire', 'error');
      return;
    }
    

    // Activer l'état de chargement
    loading.value = true;
    
    
    // Récupérer le token depuis le localStorage
    const userData = localStorage.getItem('user');
    if (!userData) {
      
      showNotificationMessage('Données utilisateur non trouvées', 'error');
      return;
    }
    
    
    // Parser l'objet JSON et extraire le token
    let token;
    try {
      const userObj = JSON.parse(userData);
      
      
      if (userObj.data && userObj.data.token) {
        token = userObj.data.token;
        
      } else {
        
        showNotificationMessage('Structure de données utilisateur invalide', 'error');
        return;
      }
    } catch (parseError) {
      
      showNotificationMessage('Format de données utilisateur invalide', 'error');
      return;
    }
    
    // Préparer les données du chatbot
    
    const chatbotData = {
      bot_name: chatbot.value.bot_name,
      type: chatbot.value.type,
      status: chatbot.value.status,
      description: chatbot.value.description,
      email: chatbot.value.email,
      display_phone_number: chatbot.value.display_phone_number,
      phone_number_id: chatbot.value.phone_number_id,
      app_id: chatbot.value.app_id,
      access_token: chatbot.value.access_token
    };
    
    
    // Créer un FormData pour envoyer les données
    
    const formData = new FormData();
    
    // Ajouter les données du chatbot
    formData.append('data', JSON.stringify(chatbotData));
    
    
    // Vérifier si un fichier d'avatar a été sélectionné
    if (chatbot.value.avatarFile) {
      
        
      formData.append('avatar', chatbot.value.avatarFile);
      
    }  
    
    
    // Envoyer la requête
    
  
    
    const response = await axios.post(
      `${apiBaseUrl}/create/strong/chatbot`,
      formData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    
    
    
    // Afficher un message de succès
    showNotificationMessage('Chatbot créé avec succès !', 'success');
    
    
    // Vérifier les paramètres d'URL pour déterminer où rediriger
    
    const returnTo = route.query.returnTo;
    const openPopupWhatsapp = route.query.openpopupwhatsapp;
    const accountId = route.query.accountId;
    const chatbotId = response.data.chatbot_id;
    
    
    
    setTimeout(() => {
      if (returnTo === 'whatsapp_management' && openPopupWhatsapp === 'true' && accountId) {
        // Rediriger vers la page de gestion WhatsApp avec les paramètres nécessaires
        
        window.location.href = `/dashboard/whatsapp_management?openpopupwhatsapp=true&accountId=${accountId}&chatbotId=${chatbotId}`;
      } else {
        // Redirection par défaut vers la liste des chatbots
        
        window.location.href = '/dashboard/chatbots';
      }
    }, 1500);
    
  } catch (err) {
    console.error('❌ ERREUR LORS DE LA CRÉATION DU CHATBOT:', err);
    
    // Afficher un message d'erreur approprié
    let errorMessage = 'Erreur lors de la création du chatbot';
    
    if (err.response) {
      // Erreur avec réponse du serveur
      console.error('📥 Détails de la réponse d\'erreur:', {
        status: err.response.status,
        statusText: err.response.statusText,
        data: err.response.data
      });
      errorMessage = err.response.data?.error || err.response.data?.message || errorMessage;
    } else if (err.request) {
      // Erreur sans réponse du serveur
      console.error('❌ Erreur de connexion au serveur');
      errorMessage = 'Impossible de contacter le serveur';
    }
    
    showNotificationMessage(errorMessage, 'error');
    
  } finally {
    // Désactiver l'état de chargement
    loading.value = false;
    
    
  }
};
</script>

<style scoped>
.create-chatbot {
  padding: 24px;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 40px;
}

.header-title h1 {
  font-size: 42px;
  font-weight: 600;
  color: #1A1D1F;
  margin: 0;
}

.header-subtitle {
  color: #6F767E;
  font-size: 15px;
  margin-top: 8px;
  font-weight: 500;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: white;
  border: 1px solid #E8ECEF;
  border-radius: 12px;
  color: #1A1D1F;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #F9FAFB;
  border-color: #D1D5DB;
}

.create-content {
  max-width: 800px;
}

.settings-section {
  background: white;
  border: 1px solid #E8ECEF;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
}

.settings-section h2 {
  font-size: 20px;
  font-weight: 600;
  color: #1A1D1F;
  margin: 0 0 24px 0;
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

.avatar-container {
  display: flex;
  flex-direction: column;
  grid-column: 1;
  grid-row: 1 / span 2;
}

.avatar-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.avatar-preview {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid #E8ECEF;
  flex-shrink: 0;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
}

.avatar-preview:hover {
  border-color: #D1D5DB;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.avatar-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  color: white;
}

.avatar-preview:hover .avatar-overlay {
  opacity: 1;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: #1A1D1F;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 8px 12px;
  border: 1px solid #E8ECEF;
  border-radius: 6px;
  font-size: 14px;
  color: #1A1D1F;
  background: white;
  width: 100%;
  box-sizing: border-box;
}

.form-group textarea {
  height: 100px;
  resize: vertical;
}

.form-group input[type="color"] {
  height: 40px;
  padding: 4px;
}

.copy-input {
  position: relative;
  display: flex;
  width: 100%;
  box-sizing: border-box;
}

.copy-input input {
  padding-right: 40px;
  width: 100%;
  flex: 1;
  box-sizing: border-box;
}

.toggle-password {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #6F767E;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.toggle-password:hover {
  color: #1A1D1F;
}

.settings-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
}

.cancel-btn,
.save-btn {
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn {
  background: white;
  border: 1px solid #E8ECEF;
  color: #1A1D1F;
}

.cancel-btn:hover {
  background: #F9FAFB;
  border-color: #D1D5DB;
}

.save-btn {
  background: #1ABB34;
  border: none;
  color: white;
}

.save-btn:hover {
  background: #159B2B;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 40px;
  color: #6F767E;
}

.error-state {
  color: #FF3B30;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 8px 0;
}

.toggle-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #6F767E;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-btn:hover {
  color: #1A1D1F;
}

.whatsapp-section {
  padding: 16px;
  margin-bottom: 16px;
}

.whatsapp-section h2 {
  margin: 0;
  font-size: 18px;
}

.whatsapp-section .section-header {
  padding: 4px 0;
}

.whatsapp-section .settings-grid {
  margin-top: 16px;
  gap: 16px;
}

@media (max-width: 768px) {
  .create-chatbot {
    padding: 16px;
  }

  .settings-grid {
    grid-template-columns: 1fr;
  }

  .content-header {
    flex-direction: column;
    gap: 16px;
  }

  .header-title h1 {
    font-size: 32px;
  }
}

:deep(.vue-tel-input) {
  border: none !important;
  display: flex !important;
  align-items: center !important;
}

:deep(.vti__dropdown) {
  padding: 0 !important;
  background: transparent !important;
  border: none !important;
  min-width: 100px !important;
}

:deep(.vti__dropdown:hover) {
  background: transparent !important;
}

:deep(.vti__selection) {
  padding: 8px 12px !important;
  background: white !important;
  border: 1px solid #E8ECEF !important;
  border-radius: 6px !important;
  margin-right: 8px !important;
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
}

:deep(.vti__selection:hover) {
  background: #F9FAFB !important;
}

:deep(.vti__flag) {
  margin-right: 8px !important;
}

:deep(.phone-input) {
  padding: 8px 12px !important;
  border: 1px solid #E8ECEF !important;
  border-radius: 6px !important;
  font-size: 14px !important;
  color: #1A1D1F !important;
  background: white !important;
  width: 100% !important;
  box-sizing: border-box !important;
  flex: 1 !important;
}

:deep(.vti__dropdown-list) {
  background: white !important;
  border: 1px solid #E8ECEF !important;
  border-radius: 6px !important;
  margin-top: 4px !important;
  max-height: 200px !important;
  overflow-y: auto !important;
}

:deep(.vti__dropdown-item) {
  padding: 8px 12px !important;
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
  cursor: pointer !important;
}

:deep(.vti__dropdown-item:hover) {
  background: #F9FAFB !important;
}

:deep(.vti__dropdown-item.selected) {
  background: #F0F2F5 !important;
}
</style> 